import * as z from "zod";
import { pageOf, pagination, uuidV7, version } from "../index";
import { elementId, emailLocale } from "./email-document";
import {
  type SendKind,
  type SendSkipReason,
  type SendStatus,
  sendKinds,
  sendPauseReasons,
  sendSkipReasons,
  sendStatuses,
} from "./engagement/enums";
import { invitationFunnelStages } from "./source/campaign-funnel";
import { accountStages } from "./source/enums";

// The sends of Guestlist (ADR-319, ADR-287 §1, §4): a draft of a published template to the people of a campaign, its
// audience counted and sampled, approved by a person in three steps (the sample, a dry run, a typed word) and sent by a
// Workflow per send, in waves by time zone and batches. A tool drafts, asks the approval and cancels; never approves.

const sendId = z.uuid();
const name = z.string().trim().min(1).max(200);

/**
 * Who of a campaign a send goes to (B-141, on its assumption): the contacts of the customers invited to it, narrowed
 * by territory, by the brands they are invited to, by the stage of the invitation and of the account, and by the role
 * of the contact. The full segments are P8.14i-b's.
 */
export const sendAudience = z
  .object({
    territories: z.array(z.string().trim().min(1).max(100)).min(1).max(50).optional(),
    brandOrgIds: z.array(z.uuid()).min(1).max(50).optional(),
    /** The stages of the invitation; without, the invitations neither withdrawn nor declined. */
    invitationStages: z.array(z.enum(invitationFunnelStages)).min(1).optional(),
    accountStages: z.array(z.enum(accountStages)).min(1).optional(),
    /** The role of the contact in party (`buyer`, `owner`…), free text: what Guestlist calls a tag for now. */
    contactRoles: z.array(z.string().trim().min(1).max(40)).min(1).max(20).optional(),
  })
  .strict();
export type SendAudience = z.output<typeof sendAudience>;

/** The contacts of a send of the booking links (`booking_links`), named one by one as the matrix picks them. */
export const bookingLinksAudience = z.object({ contactPersonIds: z.array(z.uuid()).min(1).max(1000) }).strict();
export type BookingLinksAudience = z.output<typeof bookingLinksAudience>;

const clockTime = z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/, "HH:MM");

/**
 * When a send leaves (ADR-319 §3): at once, at an instant, at a time of day where each buyer is (the next such time,
 * by the time zone of the country of the account), or at the best hour of each contact within 24 hours (the hour its
 * signals say, ADR-316; 9:30 where it is when they say nothing yet).
 */
export const sendSchedule = z.discriminatedUnion("mode", [
  z.object({ mode: z.literal("now") }),
  z.object({ mode: z.literal("at"), at: z.iso.datetime() }),
  z.object({ mode: z.literal("local_time"), time: clockTime }),
  z.object({ mode: z.literal("best_hour") }),
]);
export type SendSchedule = z.output<typeof sendSchedule>;

/**
 * An A/B test of a send (ADR-324 §2): variant A is the email as published; variant B changes one thing of it, the
 * subject or the preview text in every language it has (a language without its B text is a problem of the dry run), or
 * which of two blocks of the email is its hero (A shows `a` and hides `b`, B the other way round). A tenth of the
 * audience gets A, a tenth B; the rest gets the one with the better booking rate after `decideAfterHours`.
 */
const variantText = z.partialRecord(emailLocale, z.string().trim().min(1).max(200));
export const sendTest = z.discriminatedUnion("element", [
  z.object({ element: z.literal("subject"), b: variantText }).strict(),
  z.object({ element: z.literal("preheader"), b: variantText }).strict(),
  z
    .object({ element: z.literal("hero"), a: elementId, b: elementId })
    .strict()
    .refine((t) => t.a !== t.b, "The two heroes are two blocks"),
]);
export type SendTest = z.output<typeof sendTest>;

/**
 * The sizes of an A/B test (ADR-324 §2), constants of the contract: the share of each variant, when the winner is
 * chosen, and the sample under which the result is "too early to say" (then the rest gets A, the email as published).
 */
const TEST_VARIANT_SHARE = 0.1;
/** Each variant needs this many emails delivered, and the two together this many bookings, for a winner. */
const TEST_MIN_DELIVERED_PER_VARIANT = 100;

export const SEND_TEST = {
  variantShare: TEST_VARIANT_SHARE,
  decideAfterHours: 48,
  /**
   * A send with a test reaches at least this many: with fewer, a tenth can never reach the delivered emails a verdict
   * needs, and the rest would wait 48 hours for "Too early to say".
   */
  minRecipients: Math.ceil(TEST_MIN_DELIVERED_PER_VARIANT / TEST_VARIANT_SHARE),
  minDeliveredPerVariant: TEST_MIN_DELIVERED_PER_VARIANT,
  minBookings: 10,
} as const;

/** What the test of a send concluded: no winner yet, a winner, the same rate, or a sample too small to say. */
export const sendTestVerdicts = ["pending", "winner", "tie", "too_early"] as const;
export type SendTestVerdict = (typeof sendTestVerdicts)[number];

/** The sizes of a send and of its pace (ADR-319): a constant of the contract, as crm reads no parameter of the platform. */
export const SEND_LIMITS = {
  /** The recipients of one send; the full segments of P8.14i-b may raise it with the steps of its Workflow. */
  maxRecipients: 10_000,
  /** The recipients of one step of the Workflow. */
  batchSize: 100,
  /** The emails a batch has in flight at once. */
  concurrency: 8,
  /** The pause between two batches, for the rate of the provider (B-143). */
  paceSeconds: 2,
  /** The waves of one send: the time zones of its recipients. */
  maxWaves: 48,
  /** The people the sample of the approval shows. */
  sampleSize: 10,
  /** How long a dry run stays good for the approval. */
  dryRunMinutes: 60,
  /** The addresses of one test email. */
  testRecipients: 5,
  /** How long a paused send waits for a person each time, and how many times, before it is given up. */
  holdHours: 24,
  maxHolds: 14,
} as const;

/**
 * The breaker (ADR-319 §5): a send pauses by itself once it has `minDeliveries` deliveries and its hard bounces or its
 * complaints pass their share, or when a batch could not hand most of its emails to the provider.
 */
export const SEND_BREAKER = {
  minDeliveries: 200,
  hardBounceRate: 0.05,
  complaintRate: 0.003,
  failedRate: 0.5,
} as const;

/** What a person or a tool does to a send (ADR-319). */
export type SendAction = "edit" | "request" | "dryRun" | "approve" | "pause" | "resume" | "cancel";

/** The statuses each action is done from: crm refuses the others (422 `crm_send_status`), Guestlist does not offer them. */
const SEND_ACTIONS_FROM: Record<SendAction, readonly SendStatus[]> = {
  edit: ["draft", "awaiting_approval"],
  request: ["draft"],
  dryRun: ["awaiting_approval"],
  approve: ["awaiting_approval"],
  pause: ["approved", "sending"],
  resume: ["paused"],
  cancel: ["draft", "awaiting_approval", "approved", "sending", "paused"],
};

export const canDoSend = (status: SendStatus, action: SendAction): boolean =>
  SEND_ACTIONS_FROM[action].includes(status);

/** The time of the "local time" choice when a contact has no best hour yet. */
export const DEFAULT_LOCAL_TIME = "09:30";

/** A draft of a send of a published template to the people of a campaign, with the id from the client (ADR-046). */
export const createCrmSendInput = z.object({
  id: uuidV7,
  campaignId: z.uuid(),
  templateId: z.uuid(),
  name,
  audience: sendAudience.default({}),
  schedule: sendSchedule.default({ mode: "now" }),
  /** An A/B test of the subject, the preview text or the hero (ADR-324 §2); none by default. */
  test: sendTest.nullable().default(null),
});
export type CreateCrmSendInput = z.input<typeof createCrmSendInput>;

/** A change of a draft, or of a send waiting for its approval (which goes back to a draft). */
export const updateCrmSendInput = z.object({
  sendId,
  version,
  name: name.optional(),
  templateId: z.uuid().optional(),
  audience: sendAudience.optional(),
  schedule: sendSchedule.optional(),
  /** Null takes the test away. */
  test: sendTest.nullable().optional(),
});
export type UpdateCrmSendInput = z.input<typeof updateCrmSendInput>;

export const getCrmSendInput = z.object({ sendId });
export type GetCrmSendInput = z.input<typeof getCrmSendInput>;
/** The writes on the path of their send in gateway-guestlist: the rest of the input is the body. */
export const updateCrmSendBody = updateCrmSendInput.omit({ sendId: true });

/** The sends, the latest first; of a campaign. */
export const listCrmSendsInput = z.object({
  campaignId: z.uuid().optional(),
  status: z.enum(sendStatuses).optional(),
  ...pagination({ defaultSize: 25, maxSize: 100 }),
});
export type ListCrmSendsInput = z.input<typeof listCrmSendsInput>;

/** A wave of a send: when it leaves, and to how many. */
export const sendWaveDto = z.object({ at: z.iso.datetime(), recipients: z.number().int() });

export const crmSendDto = z
  .object({
    id: sendId,
    kind: z.enum(sendKinds),
    name: z.string(),
    campaignId: z.uuid().nullable(),
    templateId: z.uuid().nullable(),
    templateVersion: z.number().int().nullable(),
    status: z.enum(sendStatuses),
    pauseReason: z.enum(sendPauseReasons).nullable(),
    audience: z.union([sendAudience, bookingLinksAudience]),
    schedule: sendSchedule,
    recipientCount: z.number().int(),
    sentCount: z.number().int(),
    skippedCount: z.number().int(),
    failedCount: z.number().int(),
    waves: z.array(sendWaveDto),
    test: sendTest.nullable(),
    /** The variant the rest received, once the test decided (ADR-324 §2). */
    testWinner: z.enum(["a", "b"]).nullable(),
    testDecidedAt: z.iso.datetime().nullable(),
    requestedBy: z.uuid().nullable(),
    requestedAt: z.iso.datetime().nullable(),
    dryRunAt: z.iso.datetime().nullable(),
    approvedBy: z.uuid().nullable(),
    approvedAt: z.iso.datetime().nullable(),
    startedAt: z.iso.datetime().nullable(),
    sentAt: z.iso.datetime().nullable(),
    cancelledAt: z.iso.datetime().nullable(),
    createdBy: z.uuid().nullable(),
    createdAt: z.iso.datetime(),
    version: z.number().int(),
  })
  .meta({ id: "CrmSend" });
export type CrmSendDto = z.output<typeof crmSendDto>;

export const crmSendPageDto = pageOf(crmSendDto).meta({ id: "CrmSendPage" });

/** The first step of the approval: how many, who is left out and why, a sample, and when they receive it by hour. */
export const previewSendAudienceInput = z.object({ sendId });
export type PreviewSendAudienceInput = z.input<typeof previewSendAudienceInput>;

export const sendAudienceDto = z
  .object({
    recipients: z.number().int(),
    excluded: z.partialRecord(z.enum(sendSkipReasons), z.number().int()),
    /** Never how to reach them: a name, the account and the language (Q-SEC-41). */
    sample: z.array(
      z.object({
        contactPersonId: z.uuid(),
        name: z.string(),
        accountName: z.string(),
        territory: z.string().nullable(),
        locale: emailLocale,
      }),
    ),
    /** The deliveries by hour of the choice of when (the "When" preview of the composer). */
    byHour: z.array(sendWaveDto),
  })
  .meta({ id: "SendAudience" });
export type SendAudienceDto = z.output<typeof sendAudienceDto>;

/** The second step: the email drawn for the people of the sample, checked, and the word the third step asks. */
export const dryRunCrmSendInput = z.object({ sendId, version });
export type DryRunCrmSendInput = z.input<typeof dryRunCrmSendInput>;
export const dryRunCrmSendBody = dryRunCrmSendInput.omit({ sendId: true });

export const sendDryRunDto = z
  .object({
    recipients: z.number().int(),
    /** What the approval asks to type. */
    confirmation: z.string(),
    /** Who published the version the send photographs, and when (Q-SEC-42). */
    template: z.object({
      version: z.number().int(),
      publishedBy: z.uuid().nullable(),
      publishedAt: z.iso.datetime(),
    }),
    locales: z.array(
      z.object({ locale: emailLocale, recipients: z.number().int(), subject: z.string(), htmlBytes: z.number().int() }),
    ),
    /** The B side of the A/B test, as the approval covers it with the winner (ADR-324 §2): its subject by language. */
    test: z
      .object({
        element: z.enum(["subject", "preheader", "hero"]),
        locales: z.array(z.object({ locale: emailLocale, subjectB: z.string(), preheaderB: z.string() })),
        variantRecipients: z.number().int(),
      })
      .nullable(),
    /** A problem of the email that stops the approval, by language. */
    problems: z.array(z.object({ locale: emailLocale, rule: z.string(), message: z.string() })),
    version: z.number().int(),
  })
  .meta({ id: "SendDryRun" });
export type SendDryRunDto = z.output<typeof sendDryRunDto>;

/** The third step, by a person in Guestlist alone: the word of the dry run, typed. */
export const approveCrmSendInput = z.object({ sendId, version, confirmation: z.string().trim().min(1).max(40) });
export type ApproveCrmSendInput = z.input<typeof approveCrmSendInput>;
export const approveCrmSendBody = approveCrmSendInput.omit({ sendId: true });

/** Ask the approval, pause, resume, cancel: the send at the version read. */
export const crmSendActionInput = z.object({ sendId, version });
export type CrmSendActionInput = z.input<typeof crmSendActionInput>;
export const crmSendActionBody = crmSendActionInput.omit({ sendId: true });

/** An email of a template to addresses of Tomorrow (ADR-287 §4): the published version, or the draft. */
export const sendTestEmailInput = z.object({
  templateId: z.uuid(),
  locale: emailLocale,
  published: z.boolean().default(false),
  to: z.array(z.email().max(320)).min(1).max(SEND_LIMITS.testRecipients),
});
export type SendTestEmailInput = z.input<typeof sendTestEmailInput>;

export const testEmailDto = z.object({ sent: z.number().int() }).meta({ id: "TestEmail" });

/**
 * The booking links of a campaign as a send of their own (`booking_links`, ADR-319 §7): the email of the code with the
 * personal link of each contact, through the same Workflow as a campaign email. The person's request is its approval.
 */
export const sendCampaignBookingLinksInput = z.object({
  id: uuidV7,
  campaignId: z.uuid(),
  contactPersonIds: bookingLinksAudience.shape.contactPersonIds,
  schedule: sendSchedule.default({ mode: "now" }),
});
export type SendCampaignBookingLinksInput = z.input<typeof sendCampaignBookingLinksInput>;
export const sendCampaignBookingLinksBody = sendCampaignBookingLinksInput.omit({ campaignId: true });

/** The parameters of the Workflow of a send (ADR-319): who approved it, for the audit of its steps. */
export const sendCampaignParams = z.object({
  sendId,
  causedBy: z.uuid().nullable(),
  correlationId: z.string().min(1).max(200),
});
export type SendCampaignParams = z.output<typeof sendCampaignParams>;

/** The instance of the Workflow of a send has the id of the send: a second start finds the first (ADR-029 point 1). */
export const sendCampaignInstance = (id: string): string => id;

/** The event that wakes a send that waits after a pause: an event only wakes, the next step reads (ADR-097 point 10). */
export const SEND_CONTROL_EVENT = "send-control";

/** The stages of an account the audience of a send narrows to. */
export { type AccountStage, accountStages } from "./source/enums";
/** The kinds of send a person starts from Guestlist. */
export type { SendKind, SendSkipReason };
